import { Claim } from '@/lib/types';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { 
  Clock, 
  FileText, 
  Brain, 
  Shield, 
  Wrench, 
  Send,
  AlertTriangle,
  CheckCircle2
} from 'lucide-react';

const AGENT_ICONS = {
  coding: Brain,
  denial_prevention: Shield,
  submission: Send,
  appeal: FileText,
  auto_fix: Wrench,
};

interface ClaimDetailDrawerProps {
  claim: Claim | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ClaimDetailDrawer({ claim, open, onOpenChange }: ClaimDetailDrawerProps) {
  if (!claim) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[480px] sm:max-w-[480px] bg-card border-border overflow-auto scrollbar-thin">
        <SheetHeader className="pb-4">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-lg">Claim Details</SheetTitle>
            <Badge variant="outline" className="font-mono text-xs">
              {claim.id}
            </Badge>
          </div>
        </SheetHeader>

        {/* Summary */}
        <div className="space-y-6">
          <div className="glass-card p-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground text-xs">Patient</span>
                <p className="font-mono font-medium">{claim.patientInitials}</p>
              </div>
              <div>
                <span className="text-muted-foreground text-xs">Encounter</span>
                <p className="font-medium">{claim.encounterType}</p>
              </div>
              <div>
                <span className="text-muted-foreground text-xs">Payer</span>
                <p className="font-medium">{claim.payer}</p>
              </div>
              <div>
                <span className="text-muted-foreground text-xs">Facility</span>
                <p className="font-medium">{claim.facility}</p>
              </div>
            </div>
          </div>

          {/* Financials */}
          <div>
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
              Financial Summary
            </h4>
            <div className="glass-card p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Expected</span>
                <span className="font-mono font-semibold text-success">
                  ${claim.expectedAmount.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Allowed</span>
                <span className="font-mono">${claim.allowedAmount.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Patient Resp.</span>
                <span className="font-mono">${claim.patientResponsibility.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Coding */}
          <div>
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
              Coding Details
            </h4>
            <div className="glass-card p-4 space-y-3">
              <div>
                <span className="text-xs text-muted-foreground">CPT Codes</span>
                <div className="flex flex-wrap gap-2 mt-1">
                  {claim.cptCodes.map((code) => (
                    <Badge key={code} variant="secondary" className="font-mono">
                      {code}
                    </Badge>
                  ))}
                  {claim.modifiers.map((mod) => (
                    <Badge key={mod} variant="outline" className="font-mono text-primary">
                      {mod}
                    </Badge>
                  ))}
                </div>
              </div>
              <Separator />
              <div>
                <span className="text-xs text-muted-foreground">ICD-10 Codes</span>
                <div className="flex flex-wrap gap-2 mt-1">
                  {claim.icd10Codes.map((code) => (
                    <Badge key={code} variant="secondary" className="font-mono">
                      {code}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Denial Risk */}
          <div>
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
              Denial Risk Analysis
            </h4>
            <div className={cn(
              "glass-card p-4",
              claim.denialRisk < 15 && "border-success/30",
              claim.denialRisk >= 15 && claim.denialRisk < 30 && "border-warning/30",
              claim.denialRisk >= 30 && "border-destructive/30"
            )}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  {claim.denialRisk < 15 ? (
                    <CheckCircle2 className="w-4 h-4 text-success" />
                  ) : (
                    <AlertTriangle className={cn(
                      "w-4 h-4",
                      claim.denialRisk < 30 ? "text-warning" : "text-destructive"
                    )} />
                  )}
                  <span className="font-medium">Risk Score</span>
                </div>
                <span className={cn(
                  "font-mono text-lg font-bold",
                  claim.denialRisk < 15 && "text-success",
                  claim.denialRisk >= 15 && claim.denialRisk < 30 && "text-warning",
                  claim.denialRisk >= 30 && "text-destructive"
                )}>
                  {claim.denialRisk}%
                </span>
              </div>
              {claim.denialReasons.length > 0 && (
                <div className="space-y-2">
                  <span className="text-xs text-muted-foreground">Risk Factors:</span>
                  <div className="flex flex-wrap gap-2">
                    {claim.denialReasons.map((reason) => (
                      <Badge key={reason} variant="destructive" className="text-xs">
                        {reason.replace('_', ' ')}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {claim.autoFixesApplied > 0 && (
                <div className="mt-3 p-2 bg-success/10 rounded-lg border border-success/20">
                  <div className="flex items-center gap-2 text-success text-sm">
                    <Wrench className="w-4 h-4" />
                    <span>{claim.autoFixesApplied} auto-fix{claim.autoFixesApplied > 1 ? 'es' : ''} applied</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Agent Timeline */}
          <div>
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
              Agent Activity Timeline
            </h4>
            <div className="space-y-3">
              {claim.agentActions.map((action, index) => {
                const AgentIcon = AGENT_ICONS[action.agent];
                return (
                  <div 
                    key={action.id}
                    className="glass-card p-3 relative"
                  >
                    {index < claim.agentActions.length - 1 && (
                      <div className="absolute left-6 top-12 bottom-0 w-px bg-border" />
                    )}
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-secondary">
                        <AgentIcon className="w-4 h-4 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium">{action.action}</span>
                          {action.confidence && (
                            <span className="text-xs text-muted-foreground font-mono">
                              {Math.round(action.confidence * 100)}% conf
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {action.detail}
                        </p>
                        <div className="flex items-center gap-1 mt-2 text-[10px] text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          {action.timestamp.toLocaleTimeString()}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
